import { useEffect, useState } from "react";
import {
  DefaultValues,
  FieldValues,
  UseFormProps,
  useForm,
} from "react-hook-form";

import { InputAdornment } from "@mui/material";

import _ from "lodash";

import { sorted } from "./utilityFunctions";

// Use with react-hook-form's useForm() to handle errors as you register the input
const resolveObjPath = (
  path: string | string[],
  obj: Record<string, any>,
  separator = "."
) => {
  const properties = Array.isArray(path) ? path : path.split(separator);
  return properties.reduce((prev, curr) => prev && prev[curr], obj);
};

/**
 * Defaults null values to "", keeps booleans, converts numbers to strings.
 * The returned object is typed as the input type — declare numeric form
 * fields as `string` in your form-values type to match the runtime shape.
 */
export const defaultFormValues = <T extends Record<string, any>>(obj: T): T =>
  Object.entries(obj).reduce((o: Record<string, any>, [key, value]) => {
    o[key] =
      value === null || value === undefined
        ? ""
        : typeof value === "number"
          ? String(value)
          : _.isArray(value)
            ? sorted(value)
            : value;
    return o;
  }, {}) as T;

export const transformEventValue = (
  e: any,
  fn: (value: string) => string | number
) => {
  const _e = { ...e };
  _e.target.value = fn(_e.target.value);
  return _e;
};

export const useCustomRegister =
  (
    register: any,
    errors: Record<string, any>,
    options?: { onBlur?: (e: any) => void }
  ) =>
  (name: string, ops?: Record<string, any>) => {
    const { onBlur: globalOnBlur } = options || {};
    const { transform, transformOnBlur, ...checks } = ops || {};

    const { onChange, onBlur, ...registeredProps } = register(name, checks);

    const handleOnBlur = (e: any) => {
      const _e = transformOnBlur ? transformEventValue(e, transformOnBlur) : e;
      transformOnBlur && onChange(_e); // trigger value change
      onBlur && onBlur(_e);
      globalOnBlur && globalOnBlur(_e);
    };

    return {
      ...registeredProps,
      onChange: transform
        ? (e: any) => onChange(transformEventValue(e, transform))
        : onChange,
      onBlur: handleOnBlur,
      error: !!resolveObjPath(name, errors),
      helperText: resolveObjPath(name, errors)?.message,
    };
  };

// Adds a saved state to useForm, compares form values with defaultValues
export const useCustomForm = <TValues extends FieldValues = FieldValues>({
  defaultValues: dirtyDefaultValues,
  compareFunction,
  ...formArgs
}: Omit<UseFormProps<TValues>, "defaultValues"> & {
  defaultValues: TValues;
  compareFunction?: (formValues: TValues, defaultValues: TValues) => boolean;
}) => {
  const defaultValues = defaultFormValues(dirtyDefaultValues);
  const useFormValues = useForm<TValues>({
    ...(formArgs as UseFormProps<TValues>),
    defaultValues: defaultValues as DefaultValues<TValues>,
  });
  const {
    formState: { isDirty },
    getValues,
    reset,
  } = useFormValues;

  const [isSaved, setIsSaved] = useState(true);
  const compare = compareFunction ?? _.isEqual;

  useEffect(() => {
    if (!isSaved && compare(defaultFormValues(getValues()), defaultValues)) {
      reset(defaultValues as DefaultValues<TValues>);
      setIsSaved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved, defaultValues]);

  useEffect(() => {
    if (isDirty && isSaved) setIsSaved(false);
  }, [isDirty, isSaved]);

  return {
    ...useFormValues,
    isSaved,
  };
};

export const moneyValidation = {
  pattern: {
    value: /^\d+\.?(\d+)?$/,
    message: "Must be a valid dollar value",
  },
  transform: (v: string) => v.replace(/[^0-9.]/g, ""),
  transformOnBlur: (v: string) =>
    !v || isNaN(Number(v)) ? v : Number(v).toFixed(2),
};

export const intValidation = {
  pattern: {
    value: /^\d+$/,
    message: "Must be an integer",
  },
  transform: (v: string) => v.replace(/[^0-9]/g, ""),
};

export const INT32_MAX = 2_147_483_647;

export const isInt32 = (raw: string) =>
  /^\d+$/.test(raw) && Number(raw) <= INT32_MAX;

export const numberValidation = {
  pattern: {
    value: /^\d+\.?(\d+)?$/,
    message: "Must be a number",
  },
  transform: (v: string) => v.replace(/[^0-9.]/g, ""),
  transformOnBlur: (v: string) => (!v || isNaN(Number(v)) ? v : Number(v)),
};

export const dateValidation = {
  pattern: {
    value: /\d{4}-\d{2}-\d{2}/,
    message: "Invalid date",
  },
};

export const moneyAdornment = {
  startAdornment: <InputAdornment position="start">$</InputAdornment>,
};
